import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { businessService } from '@/services/business.service'
import { visitService } from '@/services/visit.service'
import { referralService } from '@/services/referral.service'
import { Link } from 'react-router-dom'
import { syncManager } from '@/lib/sync-manager'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import logo from '@/assets/logo.svg'

export function ScoutDashboardPage() {
  const { t } = useTranslation()
  const { token, user } = useAuthStore()
  const isOnline = useOnlineStatus()
  const [pendingCounts, setPendingCounts] = useState({ visits: 0, photos: 0 })
  const [showRegistrations, setShowRegistrations] = useState(false)

  useEffect(() => {
    const loadPending = async () => {
      const counts = await syncManager.getPendingCounts()
      setPendingCounts(counts)
    }
    loadPending()
    const interval = setInterval(loadPending, 5000)
    return () => clearInterval(interval)
  }, [])

  const { data: businessesData } = useQuery({
    queryKey: ['my-businesses'],
    queryFn: () => businessService.getMyBusinesses(token!),
    enabled: !!token && isOnline,
  })

  const { data: visitsData } = useQuery({
    queryKey: ['my-visits'],
    queryFn: () => visitService.list(token!),
    enabled: !!token && isOnline,
  })

  const { data: referralData } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => referralService.getStats(token!),
    enabled: !!token && isOnline,
  })

  const allReservedBusinesses = (businessesData?.businesses as any[]) || []
  const businessNameMap = new Map(allReservedBusinesses.map((b: any) => [b.id, b.name]))
  const allVisits = (visitsData?.visits as any[]) || []
  const activeVisits = allVisits.filter((v: any) => !v.isCompleted)
  const completedVisits = allVisits.filter((v: any) => v.isCompleted && !v.rejected)
  const rejectedVisits = allVisits.filter((v: any) => v.rejected)
  const visitedBusinessIds = new Set(completedVisits.map((v: any) => v.businessId))
  const reservedBusinesses = allReservedBusinesses.filter((b: any) => !visitedBusinessIds.has(b.id))

  const totalHours = completedVisits.reduce((sum: number, v: any) => {
    return sum + (v.duration || 0) / 3600
  }, 0)

  const hasPending = pendingCounts.visits > 0 || pendingCounts.photos > 0

  const handleSync = async () => {
    const result = await syncManager.sync()
    if (result.success) {
      setPendingCounts({ visits: 0, photos: 0 })
    } else {
      const counts = await syncManager.getPendingCounts()
      setPendingCounts(counts)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
              <img src={logo} alt="U-Like-It" className="h-10 w-auto hover:opacity-80 transition-opacity" />
            </a>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('scout.dashboard')}
              </h1>
              <p className="text-gray-600">
                {t('applicant.welcome', { name: user?.email })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/scout/profile" className="text-sm text-gray-600 hover:text-gray-900">
              {t('profile.title')}
            </Link>
            <LanguageSwitcher />
            <button
              onClick={() => { useAuthStore.getState().logout(); window.location.href = '/' }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Offline Queue */}
        {hasPending && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-yellow-800">{t('offline.queueTitle')}</h3>
                <div className="text-sm text-yellow-700 mt-1">
                  {pendingCounts.visits > 0 && (
                    <span>{t('offline.pendingVisits', { count: pendingCounts.visits })}</span>
                  )}
                  {pendingCounts.visits > 0 && pendingCounts.photos > 0 && <span> &bull; </span>}
                  {pendingCounts.photos > 0 && (
                    <span>{t('offline.pendingPhotos', { count: pendingCounts.photos })}</span>
                  )}
                </div>
              </div>
              {isOnline && (
                <button
                  onClick={handleSync}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                >
                  {t('offline.syncNow')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Rejected Visits Alert */}
        {rejectedVisits.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-2">{t('scout.rejectedVisitsTitle')}</h3>
            <div className="space-y-2">
              {rejectedVisits.map((visit: any) => (
                <Link key={visit.id} to={`/scout/visits/${visit.id}`} className="block p-3 bg-white rounded border border-red-100 hover:border-red-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">
                      {businessNameMap.get(visit.businessId) || visit.businessName || visit.businessId.slice(0, 8)}
                    </div>
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{t('scout.visitRejected')}</span>
                  </div>
                  {visit.rejectionReason && (
                    <p className="text-sm text-red-700 mt-1">{visit.rejectionReason}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{new Date(visit.startTime).toLocaleDateString('de-DE')}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Merchant Registrations Dialog */}
        {showRegistrations && referralData?.recentMerchants && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">{t('scout.merchantRegistrationsTitle')}</h2>
                <button
                  onClick={() => setShowRegistrations(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              <div className="p-4 overflow-y-auto space-y-2">
                {referralData.recentMerchants.length > 0 ? (
                  referralData.recentMerchants.map((m: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-100">
                      <div>
                        <span className="font-medium text-gray-900">{m.merchantName}</span>
                        <span className="text-sm text-gray-500 ml-2">{m.merchantEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{new Date(m.registeredAt).toLocaleDateString('de-DE')}</span>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{t('scout.outcome.registered')}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-4">{t('scout.noRegistrations')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {t('scout.reservedBusinesses')}
            </div>
            <div className="text-3xl font-bold text-blue-600">
              {reservedBusinesses.length}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {t('scout.activeVisits')}
            </div>
            <div className="text-3xl font-bold text-green-600">
              {activeVisits.length}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {t('scout.completedVisits')}
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {completedVisits.length}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {t('compensation.totalHours')}
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {totalHours.toFixed(1)}h
            </div>
          </div>

          <button
            onClick={() => setShowRegistrations(true)}
            className="bg-white rounded-lg shadow p-6 text-left hover:ring-2 hover:ring-green-300 transition-all"
          >
            <div className="text-sm font-medium text-gray-600 mb-1">
              {t('scout.merchantRegistrationsTitle')}
            </div>
            <div className="text-3xl font-bold text-green-600">
              {referralData?.recentMerchants?.length || 0}
            </div>
          </button>
        </div>

        {/* Bank details hint */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-blue-800">
            {t('scout.bankDetailsHint')}
          </p>
          <Link to="/scout/profile" className="text-sm font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap ml-4">
            {t('profile.title')} →
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/scout/businesses"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-6 text-center transition-colors"
          >
            <h3 className="text-xl font-semibold mb-2">
              {t('scout.findMore')}
            </h3>
            <p className="text-sm text-blue-100">
              {t('scout.availableBusinesses')}
            </p>
          </Link>

          <Link
            to="/scout/my-businesses"
            className="bg-green-600 hover:bg-green-700 text-white rounded-lg p-6 text-center transition-colors"
          >
            <h3 className="text-xl font-semibold mb-2">
              {t('scout.myBusinesses')}
            </h3>
            <p className="text-sm text-green-100">
              {t('scout.reservedBusinesses')}
            </p>
          </Link>

          <Link
            to="/scout/compensation"
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-6 text-center transition-colors"
          >
            <h3 className="text-xl font-semibold mb-2">
              {t('compensation.title')}
            </h3>
            <p className="text-sm text-purple-100">
              {t('compensation.totalHours')}: {totalHours.toFixed(1)}h
            </p>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reserved Businesses */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('scout.reservedBusinesses')}
              </h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {reservedBusinesses.length > 0 ? (
                reservedBusinesses.slice(0, 5).map((business: any) => (
                  <div key={business.id} className="p-4 hover:bg-gray-50">
                    <div className="font-medium text-gray-900">
                      {business.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {business.address}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('scout.reservedAt')}: {new Date(business.reservedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {t('scout.noBusinesses')}
                </div>
              )}
            </div>
          </div>

          {/* Recent Visits */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('scout.recentVisits')}
              </h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {completedVisits.length > 0 ? (
                completedVisits.slice(0, 5).map((visit: any) => (
                  <Link key={visit.id} to={`/scout/visits/${visit.id}`} className="block p-4 hover:bg-gray-50">
                    <div className="font-medium text-gray-900">
                      {businessNameMap.get(visit.businessId) || visit.businessName || t('scout.visit')}
                    </div>
                    <div className="text-sm text-gray-600">
                      {visit.outcome && t(`scout.outcome.${visit.outcome.toLowerCase()}`)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(visit.startTime).toLocaleDateString()} &bull; {(visit.duration / 60).toFixed(0)} {t('scout.minutes')}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {t('scout.noVisitsYet')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
